import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";
import Button from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import SalaryJournal from "../../components/Shared/SalaryJournal/SalaryJournal";
import { SalaryBatch } from "../../models/Salary";
import { parseSalaryBatch, usePollSalaryBatch } from "../../networking/salary.hooks";

interface SalaryBatchDetailsParamProps {
	salaryBatchId?: string;
}

const SalaryJournalPage: React.FC = () => {
	const { salaryBatchId } = useParams<
		keyof SalaryBatchDetailsParamProps
	>() as SalaryBatchDetailsParamProps;
	const { mutateAsync: fetchSalaryBatch } = usePollSalaryBatch();
	const { t } = useTranslation();

	const [loading, setLoading] = useState<boolean>(false);
	const [noData, setNoData] = useState<boolean>(false);
	const [journalData, setJournalData] = useState<SalaryBatch>();

	const wrapperRef = useRef(null);

	const handlePrint = useReactToPrint({
		content: () => wrapperRef.current,
	});

	useEffect(() => {
		const initSalaryBatch = async () => {
			if (salaryBatchId) {
				setLoading(true);
				const batch = await fetchSalaryBatch(salaryBatchId).catch(() => undefined);
				if (batch) {
					setJournalData(parseSalaryBatch(batch));
				} else {
					setNoData(true);
				}
				setLoading(false);
			}
		};

		initSalaryBatch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [salaryBatchId]);

	return (
		<SalaryJournalPageWrapper className="p-4 px-6">
			{loading && <LoadingIndicator />}
			{journalData && (
				<Flex margin="0 0 7px 0" direction="row" justifyContent="center">
					<JournalWrapper>
						<SalaryJournal journal={journalData} inputRef={wrapperRef} />
						<ButtonWrapper>
							<Button
								height="35px"
								margin="0 0 0 20px"
								icon="print-white"
								iconSize="12px"
								onClick={handlePrint}
							>
								{t("salary.journal.print")}
							</Button>
						</ButtonWrapper>
					</JournalWrapper>
				</Flex>
			)}
			{noData && <EmptyState margin="2rem 0" label="salary.journal.noData" />}
		</SalaryJournalPageWrapper>
	);
};

export default SalaryJournalPage;

const SalaryJournalPageWrapper = styled.div`
	@page {
		margin: 30px 0;
	}

	@media all {
		.pagebreak {
			display: none;
		}
	}

	@media print {
		.pagebreak {
			page-break-before: always;
		}
	}
`;

const JournalWrapper = styled.div`
	position: relative;
`;

const ButtonWrapper = styled.div`
	position: absolute;
	z-index: 2;
	right: 10px;
	top: 10px;
`;
